/**
 * Banner 7 + Product Rail
 *
 * Marquee mode: CSS translate3d infinite loop; JS ensures enough clones fill the rail,
 *               then toggles pause (focus, pointer, off-screen).
 * Manual mode:  horizontal scroll + prev/next arrows with infinite loop wrap-around.
 */

/**
 * @param {HTMLElement} rail
 * @param {HTMLButtonElement | null | undefined} prevBtn
 * @param {HTMLButtonElement | null | undefined} nextBtn
 * @param {{ marquee?: boolean }} opts
 */
function updateBanner7NavArrows(rail, prevBtn, nextBtn, opts) {
  const marquee = opts && opts.marquee === true;
  if (!prevBtn && !nextBtn) return;
  if (marquee) {
    if (prevBtn) prevBtn.disabled = false;
    if (nextBtn) nextBtn.disabled = false;
    return;
  }
  if (!rail) return;
  const tol = 3;
  const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
  const canScroll = maxScroll > tol;
  if (!canScroll) {
    if (prevBtn) prevBtn.disabled = true;
    if (nextBtn) nextBtn.disabled = true;
    return;
  }
  const dir = getComputedStyle(rail).direction;
  const sl = rail.scrollLeft;
  let atStart;
  let atEnd;
  if (dir === 'rtl') {
    atStart = sl >= maxScroll - tol;
    atEnd = sl <= tol;
  } else {
    atStart = sl <= tol;
    atEnd = sl >= maxScroll - tol;
  }
  if (prevBtn) prevBtn.disabled = atStart;
  if (nextBtn) nextBtn.disabled = atEnd;
}

/** @param {HTMLElement} section */
function initBanner7Carousel(section) {
  const rail = section.querySelector('[data-banner-7-rail]');
  const track = section.querySelector('[data-banner-7-rail-track]');
  if (!rail || !track) return;

  const prevBtn = section.querySelector('[data-banner-7-nav="prev"]');
  const nextBtn = section.querySelector('[data-banner-7-nav="next"]');

  const marqueeMode = section.dataset.banner7Marquee === 'true';
  const duplicated = track.dataset.banner7RailDuplicated === 'true';
  const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (marqueeMode && duplicated) {
    ensureMarqueeFill(rail, track);
  }

  if (!marqueeMode) {
    setupInfiniteManual(rail, track, prevBtn, nextBtn);
    return;
  }

  const getStep = () => {
    const firstItem = rail.querySelector('.banner-7-carousel__rail-item');
    if (!firstItem) return Math.max(240, rail.clientWidth * 0.6);

    const itemRect = firstItem.getBoundingClientRect();
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return itemRect.width + gap;
  };

  const scrollByStep = (dir) => {
    const step = getStep();
    rail.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  prevBtn?.addEventListener('click', () => scrollByStep(-1));
  nextBtn?.addEventListener('click', () => scrollByStep(1));

  updateBanner7NavArrows(rail, prevBtn, nextBtn, { marquee: true });

  const setFocusPause = (on) => section.classList.toggle('banner-7-carousel--focus-pause', on);
  const setPointerPause = (on) => section.classList.toggle('banner-7-carousel--pointer-pause', on);
  const setOffscreen = (on) => section.classList.toggle('banner-7-carousel--offscreen', on);

  if (motionOk && duplicated) {
    rail.addEventListener('focusin', () => setFocusPause(true));
    rail.addEventListener('focusout', (e) => {
      const next = e.relatedTarget;
      if (next instanceof Node && rail.contains(next)) return;
      setFocusPause(false);
    });

    rail.addEventListener('pointerdown', () => setPointerPause(true));
    rail.addEventListener('pointerup', () => setPointerPause(false));
    rail.addEventListener('pointercancel', () => setPointerPause(false));
    rail.addEventListener('pointerleave', () => setPointerPause(false));

    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        setOffscreen(!(e && e.isIntersecting));
      },
      { root: null, threshold: 0.05 }
    );
    io.observe(section);

    const shopifySection = section.closest('.shopify-section');
    if (shopifySection) {
      const unloadHandler = (/** @type {Event} */ ev) => {
        if (ev.target === shopifySection) {
          io.disconnect();
          document.removeEventListener('shopify:section:unload', unloadHandler);
        }
      };
      document.addEventListener('shopify:section:unload', unloadHandler);
    }
  }
}

/**
 * Marquee fill: if the original set of items doesn't span the visible rail,
 * clone items into BOTH halves equally so the -50% CSS animation loops seamlessly.
 */
function ensureMarqueeFill(rail, track) {
  const railWidth = rail.clientWidth;
  if (railWidth <= 0) return;

  const allItems = Array.from(track.querySelectorAll('.banner-7-carousel__rail-item'));
  if (!allItems.length) return;

  const halfCount = Math.ceil(allItems.length / 2);
  const origItems = allItems.slice(0, halfCount);
  const dupItems = allItems.slice(halfCount);

  const style = getComputedStyle(track);
  const gap = parseFloat(style.columnGap || style.gap || '0') || 0;

  let setWidth = 0;
  origItems.forEach((item) => {
    setWidth += item.getBoundingClientRect().width;
  });
  setWidth += gap * Math.max(0, origItems.length - 1);

  if (setWidth <= 0 || setWidth >= railWidth) return;

  const copiesNeeded = Math.ceil(railWidth / setWidth);

  const firstDup = dupItems[0];
  for (let c = 1; c < copiesNeeded; c++) {
    origItems.forEach((item) => {
      const clone = item.cloneNode(true);
      track.insertBefore(clone, firstDup);
    });
  }

  for (let c = 1; c < copiesNeeded; c++) {
    dupItems.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    });
  }
}

/**
 * Manual mode infinite scroll:
 * 1. Duplicate items so the track has 2 identical halves.
 * 2. When scroll reaches the second half, jump back to the first seamlessly.
 * 3. When scroll is before the first half, jump forward.
 */
function setupInfiniteManual(rail, track, prevBtn, nextBtn) {
  const items = Array.from(track.querySelectorAll('.banner-7-carousel__rail-item'));
  if (!items.length) return;

  items.forEach((item) => {
    const clone = item.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    track.appendChild(clone);
  });

  rail.style.overflowX = 'auto';
  rail.style.scrollSnapType = 'x mandatory';
  rail.style.scrollBehavior = 'auto';

  const style = getComputedStyle(track);
  const gap = parseFloat(style.columnGap || style.gap || '0') || 0;
  let oneSetWidth = 0;
  items.forEach((item) => {
    oneSetWidth += item.getBoundingClientRect().width;
  });
  oneSetWidth += gap * items.length;

  if (oneSetWidth <= 0) return;

  rail.scrollLeft = 0;

  const getStep = () => {
    const firstItem = rail.querySelector('.banner-7-carousel__rail-item');
    if (!firstItem) return 240;
    return firstItem.getBoundingClientRect().width + gap;
  };

  let isWrapping = false;

  const wrapScroll = () => {
    if (isWrapping) return;
    const sl = rail.scrollLeft;
    const maxVisible = rail.scrollWidth - rail.clientWidth;

    if (sl >= oneSetWidth) {
      isWrapping = true;
      rail.style.scrollBehavior = 'auto';
      rail.scrollLeft = sl - oneSetWidth;
      requestAnimationFrame(() => {
        rail.style.scrollBehavior = 'smooth';
        isWrapping = false;
      });
    } else if (sl <= 0 && maxVisible > 0) {
      isWrapping = true;
      rail.style.scrollBehavior = 'auto';
      rail.scrollLeft = sl + oneSetWidth;
      requestAnimationFrame(() => {
        rail.style.scrollBehavior = 'smooth';
        isWrapping = false;
      });
    }
  };

  rail.addEventListener(
    'scroll',
    () => {
      requestAnimationFrame(() => {
        wrapScroll();
        updateBanner7NavArrows(rail, prevBtn, nextBtn, { marquee: false });
      });
    },
    { passive: true }
  );

  const scrollByStep = (dir) => {
    rail.style.scrollBehavior = 'smooth';
    rail.scrollBy({ left: dir * getStep(), behavior: 'smooth' });
  };

  prevBtn?.addEventListener('click', () => scrollByStep(-1));
  nextBtn?.addEventListener('click', () => scrollByStep(1));

  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => {
      updateBanner7NavArrows(rail, prevBtn, nextBtn, { marquee: false });
    });
    ro.observe(rail);
  }

  updateBanner7NavArrows(rail, prevBtn, nextBtn, { marquee: false });
  requestAnimationFrame(() => {
    updateBanner7NavArrows(rail, prevBtn, nextBtn, { marquee: false });
  });
}

/**
 * Initialise a section once, and only once.
 * @param {HTMLElement} section
 */
function initOnce(section) {
  if (section.dataset.banner7Booted === 'true') return;
  section.dataset.banner7Booted = 'true';
  initBanner7Carousel(section);
}

/**
 * Defer the expensive init until the section approaches the viewport.
 *
 * initBanner7Carousel -> ensureMarqueeFill clones rail items until the track is wide enough
 * to loop, so it writes DOM and forces layout. Running that for a section 17th down the
 * homepage, while the page is still settling, cost a 196ms long task at 2.9s and 203ms of
 * CPU in GTmetrix - for a carousel nobody had scrolled to yet.
 *
 * Registering the observer is cheap; only the init is deferred. rootMargin gives it 300px of
 * runway so the clones exist before the rail is ever on screen, and a section already in the
 * viewport initialises immediately. Falls back to initialising straight away where
 * IntersectionObserver is missing.
 *
 * @param {HTMLElement} section
 */
function initWhenVisible(section) {
  if (!('IntersectionObserver' in window)) {
    initOnce(section);
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      if (entries[0] && entries[0].isIntersecting) {
        io.disconnect();
        initOnce(section);
      }
    },
    { rootMargin: '300px 0px' }
  );
  io.observe(section);
}

function boot(root = document) {
  root.querySelectorAll('.banner-7-carousel').forEach((section) => {
    if (section instanceof HTMLElement) initWhenVisible(section);
  });
}

/* Observers are registered at DOMContentLoaded, NOT behind requestIdleCallback. Its timeout
   is a deadline, not a delay, so rIC fired at the first idle moment and ran the full init
   right after DOMContentLoaded anyway - the deferral it looked like it provided was not real.
   Setting up an observer costs almost nothing, so there is nothing left to defer here. */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => boot(document));
} else {
  boot(document);
}
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target && 'querySelector' in target) {
    boot(/** @type {ParentNode} */ (target));
  }
});
